import { formatDateAU } from '@/lib/formatters/australian-formatters';
import type { FieldChange } from '@/lib/services/ai-analysis';

/** Matches ISO date strings the backend sends (date-only or full ISO datetime). */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(T.*)?$/;

/** Get human-readable label for a field change key */
export function getFieldChangeLabel(key: string): string {
  if (FIELD_CHANGE_LABELS[key]) return FIELD_CHANGE_LABELS[key];
  // Fallback: strip "itemInfo.", "decedentAddress.", and "decedent" prefixes, then title-case
  const stripped = key
    .replace(/^itemInfo\./, '')
    .replace(/^decedentAddress\./, '')
    .replace(/^decedent/, '');
  return stripped
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/** Human-readable labels for field change keys (dot-notation) */
const FIELD_CHANGE_LABELS: Record<string, string> = {
  title: 'Title',
  firstName: 'First Name',
  lastName: 'Last Name',
  middleName: 'Middle Name',
  email: 'Email Address',
  phoneNumber: 'Phone Number',
  address: 'Address',
  dateOfBirth: 'Date of Birth',
  dateOfDeath: 'Date of Death',
  'address.streetAddress': 'Street Address',
  'address.suburb': 'Suburb',
  'address.state': 'State',
  'address.postcode': 'Postcode',
  'address.country': 'Country',
  organisationName: 'Organisation',
  notes: 'Notes',
  grantOfRepresentationReference: 'Grant Application Number',
  grantOfRepresentationCode: 'Grant Unique Identifier',
  isBeneficiary: 'Beneficiary',
  isLegalPersonalRepresentative: 'Legal Personal Representative',
  legalPersonalRepresentativeRole: 'LPR Role',
  relationshipToDeceased: 'Relationship to deceased',
  /*
    Contact keys the scalar diff emits that had no entry, so they fell through
    to the title-cased fallback — which reads badly for the `is*` flags ("Is
    Proving Lpr"). Added here rather than in a second map on the compact review
    surface (VALE-1174), so the card path's conflict footer gains the same
    wording.
  */
  occupation: 'Occupation',
  matterNotes: 'Matter Notes',
  isOfAge: 'Of Age',
  isDeceased: 'Deceased',
  isProvingLpr: 'Proving LPR',
  nonProvingReason: 'Reason for Not Proving',
  'itemInfo.bsb': 'BSB',
  'itemInfo.accountNumber': 'Account Number',
  'itemInfo.accountName': 'Account Name',
  'itemInfo.currentBalance': 'Current Balance',
  // Type-agnostic: every liability type ('mortgage', 'credit-card', ...) reads this key
  // as a balance too, so it keeps the generic wording. VALE-802 renamed it to
  // "Date of Death Value" on the bank-account FORM, where it became a derived total.
  'itemInfo.valueAtDateOfDeath': 'Date of Death Balance',
  'itemInfo.statedBalanceAtDateOfDeath': 'Balance Stated by Bank',
  'itemInfo.accruedInterestAtDateOfDeath': 'Interest Accrued to Death',
  'itemInfo.ticker': 'Ticker',
  'itemInfo.companyName': 'Company',
  'itemInfo.numberOfShares': 'Number of Shares',
  'itemInfo.srn': 'Securityholder Reference Number (SRN)',
  'itemInfo.memberNumber': 'Member Number',
  'itemInfo.policyNumber': 'Policy Number',
  'itemInfo.streetAddress': 'Street Address',
  'itemInfo.suburb': 'Suburb',
  'itemInfo.postcode': 'Postcode',
  'itemInfo.state': 'State',
  // The backend emits this key as `sharePriceAtDateOfDeath` — `dateOfDeathPrice`
  // was never a field-change key any endpoint produced, so the label sat here
  // unreachable while the real key fell through to the title-cased fallback
  // ("Share Price At Date Of Death"). It only started rendering at all once
  // LF-6219 made the field reviewable on market-priced share types (VALE-259).
  'itemInfo.sharePriceAtDateOfDeath': 'Date of Death Price',
  'itemInfo.dateOfDeathValue': 'Date of Death Value',
  'itemInfo.currentPrice': 'Current Price',
  'itemInfo.currentValue': 'Current Value',
  'itemInfo.name': 'Name',
  'itemInfo.description': 'Description',
  'itemInfo.notes': 'Notes',
  bankAccount: 'Bank Account',
  ownershipType: 'Ownership Type',
  isNonEstate: 'Non-Estate Asset',
  institution: 'Institution'
};

/**
 * Field-change keys whose numeric value is a count, not a currency amount.
 * Without this, the generic "number >= 100 → format as $" heuristic renders a
 * share count like 4312 as "$4,312.00" in the conflict diff.
 */
const PLAIN_INTEGER_FIELD_KEYS = new Set(['itemInfo.numberOfShares']);

/** Format a number as AUD currency (shared by value + holding summaries). */
function formatAud(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(value);
}

/** Holdings summarised inline before the tail collapses to "+N more". */
const MAX_SUMMARISED_HOLDINGS = 3;

/** First non-empty, trimmed string among the candidates. */
function firstNonEmptyString(...candidates: unknown[]): string | undefined {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return undefined;
}

/**
 * True when every own value on the object is null/undefined/an empty string —
 * e.g. a holding synthesised from a source with no actual data. Such an object
 * has no readable representation, so callers should render it as absent ("-")
 * rather than falling through to a raw JSON dump.
 */
function isEffectivelyEmptyObject(obj: Record<string, unknown>): boolean {
  return Object.values(obj).every(
    (v) =>
      v === null ||
      v === undefined ||
      (typeof v === 'string' && v.trim() === '')
  );
}

/**
 * Summarise a single portfolio holding (HIN shares or crypto) as compact text,
 * e.g. "BHP — 1,200 shares @ $40.50". Falls back to JSON for unrecognised
 * shapes so nothing is silently dropped.
 */
function formatHoldingSummary(holding: unknown): string {
  if (
    holding === null ||
    typeof holding !== 'object' ||
    Array.isArray(holding)
  ) {
    return formatFieldChangeValue(holding);
  }
  const h = holding as Record<string, unknown>;
  if (isEffectivelyEmptyObject(h)) return '-';
  const name = firstNonEmptyString(
    h.ticker,
    h.tickerCompany,
    h.companyName,
    h.coinName,
    h.name,
    h.description
  );
  if (!name) return JSON.stringify(holding);
  let summary = name;
  if (typeof h.numberOfShares === 'number') {
    summary += `, ${h.numberOfShares.toLocaleString('en-AU')} shares`;
  }
  const price = [h.sharePriceAtDateOfDeath, h.currentPrice].find(
    (p): p is number => typeof p === 'number'
  );
  if (price !== undefined) summary += ` @ ${formatAud(price)}`;
  return summary;
}

/**
 * Summarise an array value. Arrays of objects (e.g. share/crypto portfolios)
 * collapse to a per-holding summary list; primitive arrays become a comma list.
 */
function formatStructuredArray(value: unknown[]): string {
  if (value.length === 0) return '-';
  if (value.every((item) => item === null || typeof item !== 'object')) {
    return value.map((item) => formatFieldChangeValue(item)).join(', ');
  }
  const shown = value
    .slice(0, MAX_SUMMARISED_HOLDINGS)
    .map(formatHoldingSummary);
  const remaining = value.length - shown.length;
  return remaining > 0
    ? `${shown.join('; ')} (+${remaining} more)`
    : shown.join('; ');
}

/** Format a field change value for display */
export function formatFieldChangeValue(
  value: unknown,
  fieldKey?: string
): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' && ISO_DATE_PATTERN.test(value)) {
    return formatDateAU(value);
  }
  if (typeof value === 'number') {
    // Count fields (e.g. number of shares) are plain integers, never currency.
    if (fieldKey && PLAIN_INTEGER_FIELD_KEYS.has(fieldKey)) {
      return value.toLocaleString('en-AU');
    }
    if (value >= 100 || value % 1 !== 0) {
      return formatAud(value);
    }
    return String(value);
  }
  if (Array.isArray(value)) {
    return formatStructuredArray(value);
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (isEffectivelyEmptyObject(obj)) return '-';
    // Bank account object
    if (obj.bsb || obj.accountNumber) {
      const parts = [];
      if (obj.bsb) parts.push(`BSB: ${obj.bsb}`);
      if (obj.accountNumber) parts.push(`Account: ${obj.accountNumber}`);
      return parts.join(', ');
    }
    // Address object
    const addrParts = [
      obj.streetAddress,
      obj.suburb,
      obj.state,
      obj.postcode,
      obj.country
    ].filter(Boolean);
    if (addrParts.length > 0) return addrParts.join(', ');
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * A payload with the reviewer's rejected fields taken out of it.
 *
 * **For a `create` row, which is the only shape that needs it.** An `update`
 * goes through {@link buildMergedProposed}, which reverts a rejected conflict to
 * the value that was already there; a create has no `fieldChanges` to walk and
 * no existing value to revert to, so the field simply must not be in what is
 * submitted. The reject stays a flag and the value stays in the reviewer's
 * record either way, which is what keeps the row on screen and the reject
 * reversible (VALE-1208).
 *
 * **A dotted key is a path, and its leaf is DELETED rather than nulled**
 * (VALE-1181). Inventory keys an `info`-backed field `itemInfo.bsb`, so a flat
 * assignment would write a literal `"itemInfo.bsb"` property beside the real
 * `itemInfo` object — a key no endpoint reads, with the rejected value applied
 * regardless. Nulling the leaf is wrong too: the inventory service reads a null
 * inside `itemInfo` as an instruction to clear that field.
 *
 * **A flat key keeps its `= null`**, which is the contacts behaviour this
 * shipped with and is right there: a contact create payload is a fixed shape,
 * and removing a key from it reads as "the extraction found nothing" rather
 * than "the reviewer turned this down".
 *
 * **Except a BOOLEAN, which clears to `false` and never to `null`**
 * (VALE-1375). Two of the flags a reviewer can reject on a contact —
 * `isBeneficiary` and `isLegalPersonalRepresentative` — are REQUIRED on
 * `CreateMatterContactDto`, carrying `@IsBoolean()` with no `@IsOptional()`.
 * Nulling one failed validation for the whole submit, so rejecting a role flag
 * during matter creation did not drop that flag: it refused to create the
 * matter at all, with an error naming a field the reviewer had not typed into.
 *
 * `false` is what "clears what the AI supplied" MEANS for a flag, and it is
 * what the row already draws for one that was never set — so the reject lands
 * as the value the reviewer was looking at, which is the whole contract. The
 * distinction is the field's TYPE in the payload, not its name, so a flag added
 * later inherits this with nothing to remember.
 */
/**
 * **The contact role flags a create payload must carry as real booleans**
 * (VALE-1617).
 *
 * `CreateMatterContactDto` declares both with `@IsBoolean()` and no
 * `@IsOptional()`, so an `undefined` or a `null` in either fails
 * class-validator — and it fails the WHOLE submit, not the one contact. The
 * reviewer sees `isLegalPersonalRepresentative must be a boolean value` naming
 * a field they never touched, on a matter that then cannot be created at all.
 *
 * Named here rather than at the call site because this file already owns the
 * fact: {@link withoutRejectedFields}' `false`-not-`null` rule exists for the
 * same two keys and the same DTO constraint. Two lists of "which flags are
 * required" would be free to drift the first time a third one is added.
 */
const REQUIRED_CONTACT_ROLE_FLAGS = [
  'isLegalPersonalRepresentative',
  'isBeneficiary'
] as const;

/**
 * **Absent means `No`** for a contact's required role flags, at the edge that
 * builds the create payload (VALE-1617).
 *
 * A preview contact reaches the submit with a role flag absent whenever the
 * extraction stated no role, which is ordinary. VALE-1375 covered the
 * neighbouring case — a *rejected* flag clearing to `null` — inside
 * {@link withoutRejectedFields}, but that infers from the payload's runtime
 * type (`typeof next[key] === 'boolean'`), so it can only correct a flag that
 * was already there. An absent one passes straight through, and a rejected
 * absent one is nulled outright.
 *
 * `CreateMatterReviewStage` already normalises these same two flags with
 * `?? false` for its contact dropdowns and did not for the payload. That
 * asymmetry is what let this ship: the screen was coherent and the request was
 * not.
 *
 * Applied AFTER {@link withoutRejectedFields}, so a reject that legitimately
 * clears a flag to `false` is preserved and only a non-boolean is corrected.
 */
export function withRequiredRoleFlags<T extends object>(contact: T): T {
  const next = { ...contact } as Record<string, unknown>;
  for (const key of REQUIRED_CONTACT_ROLE_FLAGS) {
    if (typeof next[key] !== 'boolean') next[key] = false;
  }
  return next as T;
}

export function withoutRejectedFields<T extends object>(
  proposed: T,
  rejected: string[],
  /**
   * What a given key clears TO, when the rejecting row's editor said so
   * (VALE-1375) — see `ReviewRejections.fieldClearValues`. The type-based
   * inference below cannot tell a plain boolean from a TRI-STATE, and they
   * clear to different things (`false` against unanswered), so a row that
   * knows declares it and this stops guessing.
   */
  clearValues: Record<string, unknown> = {}
): T {
  if (rejected.length === 0) return proposed;
  const next = structuredClone(proposed) as Record<string, unknown>;
  for (const key of rejected) {
    const declared = key in clearValues;
    if (!key.includes('.')) {
      next[key] = declared
        ? clearValues[key]
        : typeof next[key] === 'boolean'
          ? false
          : null;
      continue;
    }
    if (declared) {
      setNestedValue(next, key, clearValues[key]);
      continue;
    }
    const parts = key.split('.');
    const leaf = parts.pop() as string;
    let cursor: Record<string, unknown> | undefined = next;
    for (const part of parts) {
      const child: unknown = cursor?.[part];
      cursor =
        child && typeof child === 'object' && !Array.isArray(child)
          ? (child as Record<string, unknown>)
          : undefined;
      if (!cursor) break;
    }
    if (cursor) delete cursor[leaf];
  }
  return next as T;
}

/** Set a value at a dot-notation path on an object (e.g. 'itemInfo.bsb') */
export function setNestedValue(
  obj: Record<string, unknown>,
  dotPath: string,
  value: unknown
): void {
  const parts = dotPath.split('.');
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    if (
      current[key] === null ||
      current[key] === undefined ||
      typeof current[key] !== 'object'
    ) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

/** Read a value at a dot-notation path on an object. Returns undefined if any segment is missing. */
export function getNestedValue(
  obj: Record<string, unknown> | null | undefined,
  dotPath: string
): unknown {
  if (obj == null) return undefined;
  return dotPath.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

/**
 * Whether the reviewer has rejected this field change, so apply will write the
 * EXISTING value rather than the AI's.
 *
 * Exported because the reject is now offered from two places — the card path's
 * `ConflictDiffFooter` and the compact surfaces' change popover (VALE-1174) —
 * and a UI that decided "is this rejected?" for itself would be a second
 * definition of the fact {@link buildMergedProposed} acts on. It reverts
 * exactly what this returns true for, by construction: it calls this.
 *
 * **Any change kind is rejectable** (VALE-1208). It used to test `conflict`
 * alone, on the reasoning that an enrichment's `existing` is empty so there is
 * nothing to revert to, but "put the field back as it was" is exactly what
 * clearing an enrichment does, and the AI-extract review offers the reject on
 * both. Widening it is inert for every existing caller: the card path's
 * `ConflictDiffFooter` is gated on `hasConflicts` and only ever sets the flag
 * for a conflict key, so no stored `acceptedChanges` carries an enrichment.
 */
export function isFieldChangeRejected(
  change: FieldChange,
  key: string,
  acceptedChanges: Record<string, boolean>
): boolean {
  return acceptedChanges[key] === false;
}

/**
 * Build the final proposed object for an update apply payload.
 * Starts from the full proposed object (all required fields present),
 * then reverts rejected conflict fields back to the existing value.
 *
 * **A diff value is what the reviewer READS; the snapshot is what the apply
 * WRITES, and for some keys those are different types** (VALE-1576).
 *
 * `fieldChanges` is a display surface. A producer is free to project a field
 * into something a person can read — and for `institution` both of them do,
 * deliberately, emitting the institution's *name* on each side so the row
 * renders `NAB → Westpac` rather than `41 → 87`. The apply payload's
 * `institution` is an object. So reverting straight out of the diff wrote a
 * bare string into an object-typed field, `@ValidateNested()` rejected the
 * whole request with `nested property institution must be either object or
 * array`, and the reviewer was left with an extraction that could never be
 * saved and an error naming no row. Beck Legal hit exactly this and retried
 * eleven times over three hours before giving up.
 *
 * So the revert resolves against `existingSnapshot` FIRST — the same record
 * the diff was computed from, carried on the wire in payload shape
 * (`Partial<InventoryItemPreviewDto>`) precisely because it is what apply
 * accepts. `fc.existing` remains the fallback for a key the snapshot does not
 * carry, which keeps every non-projected field behaving exactly as before.
 *
 * **Resolving by SHAPE rather than by naming `institution` is the point.** The
 * defect is not that institution is special; it is that a diff value and a
 * payload value are two different things, and any future projected field
 * inherits the bug the moment someone adds one. A key-specific patch here
 * would have fixed the reported row and left the class open — which is the
 * partial-fix shape that owns the largest share of this repo's escapes.
 */
export function buildMergedProposed(
  proposed: Record<string, unknown>,
  fieldChanges: Record<string, FieldChange>,
  acceptedChanges: Record<string, boolean>,
  existingSnapshot?: Record<string, unknown> | null
): Record<string, unknown> {
  const merged = structuredClone(proposed);
  for (const [key, fc] of Object.entries(fieldChanges)) {
    if (!isFieldChangeRejected(fc, key, acceptedChanges)) continue;
    setNestedValue(merged, key, resolveRevertValue(key, fc, existingSnapshot));
  }
  return merged;
}

/**
 * What a rejected field reverts TO.
 *
 * The snapshot wins wherever it carries the key, because it is the only source
 * in payload shape. `hasNestedKey`-style presence is the test rather than
 * truthiness: a field the record genuinely holds as `null` or `''` must revert
 * to that, not fall through to the diff's rendering of it.
 */
function resolveRevertValue(
  key: string,
  change: FieldChange,
  existingSnapshot?: Record<string, unknown> | null
): unknown {
  if (!existingSnapshot) return change.existing;
  const parts = key.split('.');
  let cursor: unknown = existingSnapshot;
  for (const part of parts.slice(0, -1)) {
    if (cursor == null || typeof cursor !== 'object') return change.existing;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  if (cursor == null || typeof cursor !== 'object') return change.existing;
  const leaf = parts[parts.length - 1];
  return leaf in (cursor as Record<string, unknown>)
    ? (cursor as Record<string, unknown>)[leaf]
    : change.existing;
}

/** A row as the AI proposed it, straight off the preview response. */
interface PreviewUpdateRow<T> {
  proposed: T;
  fieldChanges?: Record<string, FieldChange>;
  /**
   * The matched record in PAYLOAD shape, which is what a rejected field reverts
   * to (VALE-1576). Always the preview row's, never the edited copy's — the
   * reviewer edits the proposal, not the record they are comparing it against.
   */
  existingSnapshot?: Record<string, unknown> | null;
}

/** The reviewer's edits to that row, if they touched it at all. */
interface EditedUpdateRow<T> {
  proposed?: T;
  fieldChanges?: Record<string, FieldChange>;
  acceptedChanges?: Record<string, boolean>;
}

/**
 * Whether a preview inventory row goes to apply as an `update` (VALE-1937).
 *
 * An `update` row always does. So does a matched `skip` row the reviewer edited: the review keeps
 * a skipped row on screen when a proposed handover names it (its only change), and an edit there
 * makes it the update it now is. Untouched, it has nothing to write, since the handover applies
 * on its own. One predicate for every host that builds the apply payload.
 */
export function isSentAsInventoryUpdate(
  row: { action: string; matchedExistingId?: number },
  editedRow: { proposed?: unknown } | undefined
): boolean {
  return (
    row.action === 'update' ||
    (row.action === 'skip' && !!row.matchedExistingId && !!editedRow?.proposed)
  );
}

/**
 * Resolve the `proposed` payload for an `update` apply row (VALE-685).
 *
 * The single definition of "what an update row actually saves", shared by the
 * contact and inventory branches of both review surfaces (the doc-intel import
 * and the client-intake stage). It exists because those four branches each
 * open-coded the merge and all four made the same mistake: they merged from the
 * PREVIEW row, so a reviewer who corrected a figure had their correction
 * silently dropped and the AI's original written to the matter instead.
 *
 * The reviewer's state wins wherever they touched the row, and the preview is
 * only ever the fallback for an untouched one:
 * - `proposed` — the values the reviewer last saved in the card;
 * - `fieldChanges` — those same values mirrored into the diff by
 *   `syncProposedIntoFieldChanges`, so an accepted conflict resolves to the
 *   reviewer's figure rather than the superseded AI one;
 * - `acceptedChanges` — the per-conflict toggles, which still revert a REJECTED
 *   conflict to the existing value.
 *
 * **`rejectedKeys` is the Y6 popover's half of that last bullet, and it is a
 * parameter rather than the caller's business** (VALE-1375).
 *
 * A reject reaches this function by two routes that look like one fact and are
 * stored as two. The card path's `ConflictDiffFooter` writes
 * `editedRow.acceptedChanges`; the change popover writes the `useReviewRejections`
 * store, which nothing here can see. With the compact review flag on, the card
 * path never renders, so `acceptedChanges` stays empty and a popover reject
 * reverted nothing: the row greyed on screen and the AI's value was written to
 * the matter regardless.
 *
 * VALE-1181 found exactly this and fixed it at ONE of the two call sites, by
 * folding the store's keys into `acceptedChanges` inline in the inventory
 * branch of the import container. The contacts branch beside it kept calling
 * this function with the edited row untouched and kept the bug, which is the
 * repeat-single-surface-fix pattern VALE-1375 exists to end. Taking the keys
 * here is what makes that impossible to get right on one branch and wrong on
 * its sibling: a caller that has rejections passes them, and the merge itself
 * has one definition.
 *
 * Folded ON TOP of any card-path flags rather than replacing them, so a
 * reviewer who used both routes gets the union.
 */
export function buildReviewedUpdateProposed<T extends object>(
  previewRow: PreviewUpdateRow<T>,
  editedRow: EditedUpdateRow<T> | undefined,
  rejectedKeys: readonly string[] = []
): T {
  const reviewedProposed = editedRow?.proposed || previewRow.proposed;
  const reviewedFieldChanges =
    editedRow?.fieldChanges ?? previewRow.fieldChanges;
  if (!reviewedFieldChanges) return reviewedProposed;
  return buildMergedProposed(
    reviewedProposed as unknown as Record<string, unknown>,
    reviewedFieldChanges,
    mergeRejectedKeys(editedRow?.acceptedChanges, rejectedKeys),
    // The PREVIEW row's snapshot, deliberately — see PreviewUpdateRow.
    previewRow.existingSnapshot
  ) as unknown as T;
}

/**
 * The two reject routes as one `acceptedChanges` map.
 *
 * Exported because a CREATE row needs the same union and cannot go through
 * {@link buildReviewedUpdateProposed} to get it — it has no `fieldChanges` for
 * the merge to walk, so it reaches {@link withoutRejectedFields} instead
 * (VALE-1375).
 */
export function mergeRejectedKeys(
  acceptedChanges: Record<string, boolean> | undefined,
  rejectedKeys: readonly string[]
): Record<string, boolean> {
  if (rejectedKeys.length === 0) return acceptedChanges ?? {};
  const merged = { ...acceptedChanges };
  for (const key of rejectedKeys) merged[key] = false;
  return merged;
}
